/**
 * What the engine-staff routes answer: the ingredient catalog, versioned rule sets, evaluation
 * runs and promotion. Type aliases so they assign to the Worker's JSON body. Rules travel as JSON
 * text, because the admin app may not import the engine.
 */
import { z } from "zod";
import { staffReason } from "./admin.ts";
import type { AdminAuditRow } from "./admin.ts";

export const INGREDIENT_ID = /^[a-z][a-z0-9_]{0,63}$/;

export type AdminIngredientStatus = "active" | "retired";

export type AdminEngineStatus = "candidate" | "live" | "retired";

export type AdminIngredient = {
  readonly id: string;
  readonly name: string;
  readonly form: string;
  readonly unit: string;
  readonly dailyMin: number;
  readonly dailyMax: number;
  readonly blendable: boolean;
  readonly isProteinBase: boolean;
  readonly pharmacyPartnerId: string | null;
  readonly pharmacyName: string | null;
  readonly addonPriceCents: number | null;
  readonly status: AdminIngredientStatus;
  readonly createdAt: string;
};

export type AdminPharmacy = {
  readonly id: string;
  readonly name: string;
  readonly status: "active" | "paused";
};

export type AdminCatalog = {
  readonly ingredients: readonly AdminIngredient[];
  readonly pharmacies: readonly AdminPharmacy[];
};

export type AdminEngineVersion = {
  readonly id: string;
  readonly version: string;
  readonly ruleSetVersion: string;
  readonly notes: string | null;
  readonly status: AdminEngineStatus;
  readonly promotedAt: string | null;
  readonly retiredAt: string | null;
  readonly createdAt: string;
};

export type AdminEngineVersions = {
  readonly versions: readonly AdminEngineVersion[];
  readonly live: {
    readonly version: AdminEngineVersion;
    readonly rulesJson: string;
  } | null;
  /** Provisional rules, for the editor when nothing has been saved yet. */
  readonly starterRulesJson: string;
  readonly history: readonly AdminAuditRow[];
};

export type AdminEngineVersionDetail = {
  readonly version: AdminEngineVersion;
  readonly rulesJson: string;
};

export type AdminEvaluationCaseRow = {
  readonly id: string;
  readonly label: "good" | "bad";
  readonly reason: string;
  readonly createdAt: string;
};

export type AdminEvaluationSet = {
  readonly versions: readonly AdminEngineVersion[];
  readonly cases: readonly AdminEvaluationCaseRow[];
};

export type AdminFormulaLine = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
};

export type AdminDoseDisagreement = {
  readonly ingredientId: string;
  readonly drafted: number;
  readonly approved: number;
};

export type AdminCaseComparison = {
  readonly caseId: string;
  readonly label: "good" | "bad";
  readonly reason: string;
  readonly generated: readonly AdminFormulaLine[];
  readonly approved: readonly AdminFormulaLine[];
  readonly missing: readonly string[];
  readonly extra: readonly string[];
  readonly doseDisagreements: readonly AdminDoseDisagreement[];
};

export type AdminSafetyFailure = {
  readonly caseId: string;
  readonly kind: string;
  readonly severity: string;
  readonly note: string;
};

export type AdminEvaluationRun = {
  readonly version: AdminEngineVersion;
  readonly caseCount: number;
  readonly safety: { readonly blocks: number; readonly warnings: number };
  readonly agreement: {
    readonly goodCases: number;
    readonly sameIngredients: number;
    readonly dosesAgreed: number;
    readonly dosesCompared: number;
    readonly reproducedBad: readonly string[];
  };
  readonly cases: readonly AdminCaseComparison[];
  readonly promote: boolean;
  readonly promoteReason: "no_new_safety_failures" | "new_safety_failures" | "no_cases";
  readonly newFailures: readonly AdminSafetyFailure[];
};

const Reason = z.string().transform((value, ctx) => {
  const reason = staffReason(value);

  if (reason === null) {
    ctx.addIssue({ code: "custom", message: "reason_required" });

    return z.NEVER;
  }

  return reason;
});

const IngredientFields = z.object({
  name: z.string().trim().min(1).max(120),
  form: z.string().trim().min(1).max(60),
  unit: z.string().trim().min(1).max(24),
  dailyMin: z.number().finite().nonnegative(),
  dailyMax: z.number().finite().positive(),
  blendable: z.boolean(),
  isProteinBase: z.boolean(),
  pharmacyPartnerId: z.string().trim().min(1).nullable(),
  addonPriceCents: z.number().int().nonnegative().nullable(),
  status: z.enum(["active", "retired"]),
  reason: Reason,
});

export type AdminIngredientWrite = {
  readonly name: string;
  readonly form: string;
  readonly unit: string;
  readonly dailyMin: number;
  readonly dailyMax: number;
  readonly blendable: boolean;
  readonly isProteinBase: boolean;
  readonly pharmacyPartnerId: string | null;
  readonly addonPriceCents: number | null;
  readonly status: AdminIngredientStatus;
  readonly reason: string;
};

export type AdminIngredientCreate = AdminIngredientWrite & { readonly id: string };

function parseJson<T>(text: string, schema: z.ZodType<T>): T | null {
  let raw: unknown;

  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }

  const parsed = schema.safeParse(raw);

  return parsed.success ? parsed.data : null;
}

const IngredientCreate = IngredientFields.extend({
  id: z.string().regex(INGREDIENT_ID),
});

export function parseIngredientCreate(text: string): AdminIngredientCreate | null {
  const parsed = parseJson(text, IngredientCreate);

  if (parsed === null || parsed.dailyMin > parsed.dailyMax) return null;

  return parsed;
}

export function parseIngredientWrite(text: string): AdminIngredientWrite | null {
  const parsed = parseJson(text, IngredientFields);

  if (parsed === null || parsed.dailyMin > parsed.dailyMax) return null;

  return parsed;
}

export type AdminRuleSetWrite = {
  readonly version: string;
  readonly rulesJson: string;
  readonly notes: string | null;
  readonly reason: string;
};

const RuleSetWrite = z.object({
  version: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Za-z0-9._-]+$/),
  rulesJson: z.string().min(2).max(200_000),
  notes: z.string().trim().max(500).nullable(),
  reason: Reason,
});

export function parseRuleSetWrite(text: string): AdminRuleSetWrite | null {
  return parseJson(text, RuleSetWrite);
}

export type AdminStaffReason = {
  readonly reason: string;
};

const StaffReasonBody = z.object({ reason: Reason });

export function parseStaffReasonBody(text: string): AdminStaffReason | null {
  return parseJson(text, StaffReasonBody);
}

export type AdminRateWrite = {
  readonly caseId: string;
  readonly label: "good" | "bad";
  readonly reason: string;
  readonly asNew: boolean;
  readonly generatedJson: string | null;
};

const RateWrite = z.object({
  caseId: z.string().trim().min(1).max(80),
  label: z.enum(["good", "bad"]),
  reason: Reason,
  asNew: z.boolean().optional(),
  generatedJson: z.string().min(2).max(20_000).nullable().optional(),
});

export function parseRateWrite(text: string): AdminRateWrite | null {
  const parsed = parseJson(text, RateWrite);

  if (parsed === null) return null;

  return {
    caseId: parsed.caseId,
    label: parsed.label,
    reason: parsed.reason,
    asNew: parsed.asNew === true,
    generatedJson: parsed.generatedJson ?? null,
  };
}
